/* OSManagementRenderer.js */

"use strict";
/* global Slick */
const _ = require("lodash");
const {
    SSH_KEX_ALGO,
    SSH_KEX_DEF,
    SSH_CIPHER_DEF,
    SSH_CIPHERS,
    SSH_MAC_DEF,
    SSH_MACS,
} = require("./OSManagementConstants");
const uiCore = require("@gluenetworks/steve_ui_core");
const CheckboxControl = uiCore.Control.Boolean.InputCheckbox;
const InputNumberControl = uiCore.Control.Textfield.InputNumber;
const MultiSelect = require("../../user/ManageUsers/OrgsComboBoxComponent");

const uiUtils = require("../../../uiutils");
const formatter = require("./cellFormaters");

const editDialog = require("./veil/addEditImageServer/AddEditImageServerComponent");
const ShowKeysDialog = require("./veil/showKeys/ShowKeysComponent");

const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;

const SingleCombobox = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single
    .ComboBox;

const Forms = require("@gluenetworks/forms");
const FormsComponent = Forms.ui.component;

const ENABLE_FILE_SERVERS_TITLE = "Enable File Servers";
const ENABLE_FILE_SERVERS_WITH_UPSTREAM_WARNING =
    "WARNING - Creation of File Servers in this organization will invalidate any current Catalog entries, Plans, and device File Server assignments for this organization and its subordinate organizations. Are you sure you wish to continue?";
const ENABLE_FILE_SERVERS_WITH_NO_UPSTREAM_WARNING =
    "This will enable File Servers in this organization. Once file servers are defined, OS Management will be functional for this organization and its subordinate organizations.";

const DISABLE_FILE_SERVERS_TITLE = "Disable File Servers";
const DISABLE_FILE_SERVERS_WARNING_MESSAGE = `
    WARNING - This will delete all File Servers in this organization and its subordinate organizations. All current Catalog entries, OS Plans and device File Server assignments will be invalidated.
    <br /><br /> If File Servers exist in a parent organization, they will now be used. Otherwise, OS Manager will not be fully functional until File Servers are defined for this organization.
    <br /><br /> Are you sure you wish to continue?
`;

const DELETE_CONFIRMATION = (name) =>
    `Are you sure you want to permanently delete "${name}"? All data will be lost for this Image Server.`;
const DELETE_MAIN_CONFIRMATION =
    "Are you sure you want to permanently delete the MAIN server? All data will be lost and all remote servers will be deleted.";

const REFRESH_CONFIRMATION = (name) => `Are you sure you want to refresh "${name}" server?`;
const REFRESH_MAIN_CONFIRMATION = "Are you sure you want to refresh the MAIN server?";

class OSManagementRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("os-management-gl");
        this._rootElem.component = model;

        this._headerElem = document.createElement("h1");
        this._headerElem.innerText = "OS Manager";
        this._rootElem.appendChild(this._headerElem);

        this._vendorDropdown = new SingleCombobox({
            darkTheme: true,
            autoSelect: false,
        });

        this._osDropdown = new SingleCombobox({
            darkTheme: true,
            autoSelect: false,
        });

        this._osmOptionFormContainer = document.createElement("div");
        this._osmOptionFormContainer.classList.add("osm-option-form");

        this._noOptionsEl = document.createElement("div");
        this._noOptionsEl.classList.add("no-options");
        this._noOptionsEl.innerHTML = "No options available.";

        this._vendorOsDropDownsEl = document.createElement("div");
        this._vendorOsDropDownsEl.classList.add("options-dropdowns");

        this._formsComponent = new FormsComponent();

        this._osmOptionFormContainer.appendChild(this._formsComponent.renderer.rootElement);

        /* Form controls: Start */
        this._slickTableContainer = document.createElement("image-slick-container");
        this._addFileServerButton = new ButtonControl({
            label: "Add File Server+",
            active: true,
            class: "add-new-server",
        });
        this._downloadActivityLogsBtn = new ButtonControl({
            darkTheme: true,
            label: "Download File Server Activity",
            class: "download-btn",
            visible: false,
        });

        this._resetSShPrefsBtn = new ButtonControl({
            darkTheme: true,
            label: "Reset SSH to default settings",
            class: "reset-ssh",
            disabled: true,
        });

        this._defaultMessageContainer = document.createElement("label");
        this._defaultMessageContainer.innerText = "No Servers Available";
        this._defaultMessageContainer.classList.add("no-servers", "hide");

        this._fileServersEnabledCheckBox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Enable File Servers for this Organization",
            disabled: false,
        });

        this._enableCatalogsCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Enable Catalog",
            disabled: false,
        });

        this._enableCiscoValidationCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Enable vendor API image validation",
            disabled: false,
        });

        this._enableAnonymousFtpCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Anonymous FTP",
            disabled: false,
        });

        this._skipValidationCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: 'Skip device validation for "Transfer Only" plan',
            disabled: false,
        });

        this._linearJobExecutionCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Linear Job Execution",
            disabled: false,
        });
        this._reExecutionCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Allow re-execution of failed plans",
            disabled: false,
        });
        this._abortOnFailureCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Abort on Failure - Greater than",
            disabled: false,
        });
        this._failureThresholdInput = new InputNumberControl({
            id: "aof-inc",
            name: "aof-inc",
            placeholder: "0",
            disabled: false,
            darkTheme: true,
            min: 0,
            max: 100,
        });

        //region file server ports
        this._ftpCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "FTP Port",
            disabled: false,
        });
        this._tftpCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "TFTP Port",
            disabled: false,
        });
        this.ftpInput = new InputNumberControl({
            id: "file-server-ftp-port",
            name: "file-server-ftp-port",
            placeholder: "",
            disabled: false,
            darkTheme: true,
            min: 0,
            max: 65535,
            onlyInteger: true,
        });
        this.tftpInput = new InputNumberControl({
            id: "file-server-tftp-port",
            name: "file-server-tftp-port",
            placeholder: "",
            disabled: false,
            darkTheme: true,
            min: 0,
            max: 65535,
            onlyInteger: true,
        });
        this.scpInput = new InputNumberControl({
            id: "file-server-scp-port",
            name: "file-server-scp-port",
            placeholder: "",
            disabled: false,
            darkTheme: true,
            min: 0,
            max: 65535,
            onlyInteger: true,
        });
        this.adminPortInput = new InputNumberControl({
            id: "file-server-admin-port",
            name: "file-server-admin-port",
            placeholder: "",
            disabled: false,
            darkTheme: true,
            min: 0,
            max: 65535,
            onlyInteger: true,
        });
        //endregion

        this._actionsContainer = document.createElement("actions-container");
        this._cancelBtn = new ButtonControl({
            disabled: true,
            invalid: false,
            visible: true,
            darkTheme: true,
            label: "Cancel",
        });
        this._saveBtn = new ButtonControl({
            disabled: true,
            invalid: false,
            visible: true,
            darkTheme: true,
            label: "Save",
        });

        /* ssh settings */
        this._sshKexAlgorithmsEl = new MultiSelect({darkTheme: true});
        this._sshCipherEl = new MultiSelect({darkTheme: true});
        this._sshMacEl = new MultiSelect({darkTheme: true});
        this._sshV1Checkbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Enable SSH v1 Protocol",
            disabled: false,
        });
        /* Form controls: End */

        this._tableOptions = {
            explicitInitialization: true,
            editable: false,
            enableAddRow: false,
            enableCellNavigation: true,
            enableColumnReorder: false,
            forceFitColumns: true,
            enableAsyncPostRender: true,
            asyncEditorLoading: false,
            rowHeight: 45,
            resizable: true,
            autoHeight: true,
        };

        const {IMAGE_SERVER_STATUS} = this._model.OsImageServersEntity || {};
        this._columns = [
            {
                id: "enabled",
                width: 70,
                name: "Enabled",
                toolTip: "Enabled",
                field: "enabled",
                resizable: false,
                formatter: formatter.checkboxFormatter,
                cssClass: "centered",
                headerCssClass: "centered",
            },
            {
                id: "name",
                width: 125,
                name: "Name",
                toolTip: "Name",
                field: "name",
                formatter: formatter.cellWithTitle,
            },
            {
                id: "identifier",
                width: 115,
                name: "ID",
                toolTip: "ID",
                field: "identifier",
                resizable: false,
                cssClass: "centered",
                headerCssClass: "centered",
                formatter: formatter.cellWithTitle,
            },
            {
                id: "ip",
                width: 115,
                name: "IPv4 Address",
                toolTip: "IPv4 Address",
                field: "ip",
                resizable: false,
                formatter: formatter.cellWithTitle,
            },
            {
                id: "ipv6",
                width: 350,
                name: "IPv6 Address",
                toolTip: "IPv6 Address",
                field: "ipv6",
                resizable: false,
                formatter: formatter.cellWithTitle,
            },
            {
                id: "status",
                width: 110,
                name: "Status",
                toolTip: "Status",
                field: "status",
                resizable: false,
                cssClass: "centered",
                headerCssClass: "centered",
                formatter: formatter.valueMapFormatter(IMAGE_SERVER_STATUS),
            },
            {
                id: "percentFull",
                width: 60,
                name: "Usage",
                toolTip: "Usage",
                field: "percentFull",
                resizable: false,
                formatter: formatter.usageColumnFormatter,
                cssClass: "centered",
                headerCssClass: "centered",
            },
            {
                id: "actions",
                //width: 170,  //width will be updated in cell formatter according to buttons amount
                name: "Actions",
                toolTip: "Actions",
                field: "identifier",
                resizable: false,
                cssClass: "actions",
                headerCssClass: "centered",
                formatter: formatter.actionButtonsFormatter(this._model),
            },
        ];

        this._clickHandler = _.debounce(this._clickHandler.bind(this), 500, {
            leading: true,
            trailing: false,
        });
        this._fitTableSize = this._fitTableSize.bind(this);
        this._onFeedUpdate = _.debounce(this._onFeedUpdate.bind(this), 200);
        this._onDirtyState = _.debounce(this._onDirtyState.bind(this));

        this._onOsDropdownChange = this._onOsDropdownChange.bind(this);
        this._onVendorDropdownChange = this._onVendorDropdownChange.bind(this);
        this._onFormsPendingPatchesChanged = _.debounce(
            this._onFormsPendingPatchesChanged.bind(this),
            100
        );

        this._slickTable = new Slick.Grid(
            this._slickTableContainer,
            {},
            this._columns,
            this._tableOptions
        );
        this._onSSHChange = this._onSSHChange.bind(this);

        this._registerListeners();
    }

    get rootElement() {
        return this.getRootElement();
    }
    get model() {
        return this.getModel();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        document.addEventListener("click", this._clickHandler);
        window.addEventListener("resize", this._fitTableSize);
        this._saveBtn.on("UserClick", this._submitAction.bind(this));
        this._cancelBtn.on("UserClick", this._cancelAction.bind(this));

        this._fileServersEnabledCheckBox.on("UserValue", this._fileServersEnabledChange.bind(this));

        this._addFileServerButton.on("UserClick", this._addNewFileServer.bind(this));
        this._downloadActivityLogsBtn.on("UserClick", this._downloadActivityLogs.bind(this));

        this._resetSShPrefsBtn.on("UserClick", this._onResetSSHPrefs.bind(this));

        this._enableCatalogsCheckbox.on("UserValue", this._catalogEnabledChange.bind(this));
        this._enableCiscoValidationCheckbox.on(
            "UserValue",
            this._ciscoValidationEnabledChange.bind(this)
        );
        this._enableAnonymousFtpCheckbox.on(
            "UserValue",
            this._anonymousFtpEnabledChange.bind(this)
        );

        this._tftpCheckbox.on("UserValue", this._tftpEnabledChange.bind(this));
        this._tftpCheckbox.on("UserValue", (checkboxValue) => {
            this.tftpInput.disabled = !checkboxValue;
        });
        this._tftpCheckbox.on("Value", (checkboxValue) => {
            this.tftpInput.disabled =
                this._model._osImageServerPrefs._tenantId !== this._model.activeOrgId ||
                !checkboxValue;
        });
        this._ftpCheckbox.on("UserValue", this._ftpEnabledChange.bind(this));
        this._ftpCheckbox.on("UserValue", (checkboxValue) => {
            this.ftpInput.disabled = !checkboxValue;
            this._enableAnonymousFtpCheckbox.disabled = !checkboxValue;
        });
        this._ftpCheckbox.on("Value", (checkboxValue) => {
            this.ftpInput.disabled =
                this._model._osImageServerPrefs._tenantId !== this._model.activeOrgId ||
                !checkboxValue;
        });
        // this._anonymousFtpCheckbox.on("Value", (checkboxValue) => {
        //     this.anonymousFtpCheckbox.disabled = !checkboxValue;
        // });
        this.scpInput.on("UserValue", this._scpPortChange.bind(this));
        this.adminPortInput.on("UserValue", this._hostPortChange.bind(this));
        this.ftpInput.on("UserValue", this._tftpPortChange.bind(this));
        this.tftpInput.on("UserValue", this._ftpPortChange.bind(this));

        this._sshCipherEl.on("UserValue", this._onSSHChange);
        this._sshKexAlgorithmsEl.on("UserValue", this._onSSHChange);
        this._sshMacEl.on("UserValue", this._onSSHChange);
        this._sshV1Checkbox.on("UserValue", this._onSSHChange);

        this._linearJobExecutionCheckbox.on("UserValue", this._linearJobExecutionChange.bind(this));
        this._reExecutionCheckbox.on("UserValue", this._reExecutionChange.bind(this));
        this._skipValidationCheckbox.on("UserValue", this._skipValidationChange.bind(this));

        this._abortOnFailureCheckbox.on("UserValue", this._abortOnFailureChange.bind(this));
        this._failureThresholdInput.on("UserValue", this._failureThresholdInputChange.bind(this));

        this._vendorDropdown.on("UserValue", this._onVendorDropdownChange);
        this._osDropdown.on("UserValue", this._onOsDropdownChange);

        this._formsComponent.on("PendingPatchesChanged", this._onFormsPendingPatchesChanged);

        this._model.on("Organization", this._onOrganizationChange.bind(this));
        this._model.on("DataUpdated", this._onDataUpdated.bind(this));
        this._model.on("DirtyState", this._onDirtyState);
        this._model.on("OrganizationDataChange", this._onOrgDataChanged.bind(this));
    }

    _registerFeedListeners(feed) {
        feed.on("ChangeFeedInitialized", (data, feed /*, reInit*/) => {
            this._onFeedUpdate(feed);
        })
            .on("EntityPropertiesUpdated", (entity, patches, props, feed) => {
                this._onFeedUpdate(feed);
            })
            .on("EntityAdded", (entity, properties, feed) => {
                this._onFeedUpdate(feed);
            })
            .on("EntityRemoved", (entity, cause, feed) => {
                this._onFeedUpdate(feed);
            })
            .once("ShutdownStarted", () => {
                feed.removeAllListeners("EntityPropertiesUpdated")
                    .removeAllListeners("EntityAdded")
                    .removeAllListeners("EntityRemoved")
                    .removeAllListeners("ChangeFeedInitialized");
            });
    }

    _unregisterListeners() {
        document.removeEventListener("click", this._clickHandler);
        window.removeEventListener("resize", this._fitTableSize);
        this._saveBtn.removeAllListeners("UserClick");
        this._cancelBtn.removeAllListeners("UserClick");

        this._fileServersEnabledCheckBox.removeAllListeners("UserValue");
        this._addFileServerButton.removeAllListeners("UserClick");
        this._downloadActivityLogsBtn.removeAllListeners("UserClick");
        this._resetSShPrefsBtn.removeAllListeners("UserClick");
        this._enableCatalogsCheckbox.removeAllListeners("UserValue");
        this._enableCiscoValidationCheckbox.removeAllListeners("UserValue");
        this._enableAnonymousFtpCheckbox.removeAllListeners("UserValue");
        this._ftpCheckbox.removeAllListeners("UserValue");
        this._tftpCheckbox.removeAllListeners("UserValue");
        this.tftpInput.removeAllListeners("UserValue");
        this.ftpInput.removeAllListeners("UserValue");
        this.scpInput.removeAllListeners("UserValue");
        this.adminPortInput.removeAllListeners("UserValue");

        this._sshCipherEl.removeAllListeners("UserValue");
        this._sshKexAlgorithmsEl.removeAllListeners("UserValue");
        this._sshMacEl.removeAllListeners("UserValue");
        this._sshV1Checkbox.removeAllListeners("UserValue");

        this._vendorDropdown.removeAllListeners("UserChange");
        this._osDropdown.removeAllListeners("UserChange");
        this._formsComponent.removeAllListeners("PendingPatchesChanged");

        this._linearJobExecutionCheckbox.removeAllListeners("UserValue");
        this._reExecutionCheckbox.removeAllListeners("UserValue");
        this._skipValidationCheckbox.removeAllListeners("UserValue");
        this._abortOnFailureCheckbox.removeAllListeners("UserValue");
        this._failureThresholdInput.removeAllListeners("UserValue");
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    getRootElement() {
        return this._rootElem;
    }
    getModel() {
        return this._model;
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (this._model) {
            return this._model
                .whenModelIsReady()
                .then(() => {
                    const imageServersFeed = this.model.imageServersFeed;
                    if (this._rootElem) {
                        this._renderPrefs();
                        const kexopts = SSH_KEX_ALGO.map((val) => {
                            return {
                                label: val,
                                value: val,
                            };
                        });
                        this._sshKexAlgorithmsEl.setOptions(kexopts);

                        const ciphopts = SSH_CIPHERS.map((val) => {
                            return {
                                label: val,
                                value: val,
                            };
                        });
                        this._sshCipherEl.setOptions(ciphopts);

                        const macopts = SSH_MACS.map((val) => {
                            return {
                                label: val,
                                value: val,
                            };
                        });
                        this._sshMacEl.setOptions(macopts);

                        if (this._model._osmOptionsAvailable) {
                            this._vendorDropdown.setOptions(
                                this._model.generateOsmOptionsVendorDDOpts()
                            );
                            this._osDropdown.setOptions(
                                this._model.generateOsmOptionsOsDDOpts("*")
                            );
                            this._vendorDropdown.setValue("*", true);
                            this._onVendorDropdownChange("*");
                            this._formsComponent.renderer.render();
                        }
                        this._showHideOptionsForm();

                        this._initializeSlickGrid(this._convertFeedToSlickData(imageServersFeed));
                        this._registerFeedListeners(imageServersFeed);
                        // this._toggleDownloadActivityLogs(!_.isEmpty(imageServersFeed.cache));
                        this._fitTableSize();
                        this._setPristineProperties();
                        this._setResetSSHBtnState();
                    }
                })
                .catch(console.error);
        }
    }

    shutdown() {
        this._unregisterListeners();
        if (this._slickTable.initialized) {
            this._slickTable.destroy();
        }
        const controls = [
            this._addFileServerButton,
            this._downloadActivityLogsBtn,
            this._fileServersEnabledCheckBox,
            this._enableCatalogsCheckbox,
            this._enableCiscoValidationCheckbox,
            this._enableAnonymousFtpCheckbox,
            this._ftpCheckbox,
            this._tftpCheckbox,
            this.tftpInput,
            this.scpInput,
            this.ftpInput,
            this.adminPortInput,
            this._linearJobExecutionCheckbox,
            this._reExecutionCheckbox,
            this._skipValidationCheckbox,
            this._abortOnFailureCheckbox,
            this._failureThresholdInput,
            this._cancelBtn,
            this._saveBtn,
            this._sshKexAlgorithmsEl,
            this._sshMacEl,
            this._sshV1Checkbox,
            this._sshCipherEl,
        ];

        return Promise.all(_.invokeMap(controls, "shutdown")).then(() => {
            // TODO
            this._headerElem.remove();
            this._headerElem = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
            this._slickTable = undefined;
            this._clickHandler = undefined;
            this._tableOptions = undefined;
            this._slickTableContainer.remove();
            this._slickTableContainer = undefined;

            this._enableCatalogsCheckbox = undefined;
            this._enableCiscoValidationCheckbox = undefined;
            this._enableAnonymousFtpCheckbox = undefined;
            this._ftpCheckbox = undefined;
            this._tftpCheckbox = undefined;
            this.tftpInput = undefined;
            this.ftpInput = undefined;
            this.adminPortInput = undefined;
            this.scpInput = undefined;
            this._linearJobExecutionCheckbox = undefined;
            this._reExecutionCheckbox = undefined;
            this._skipValidationCheckbox = undefined;
            this._abortOnFailureCheckbox = undefined;
            this._failureThresholdInput = undefined;
            this._cancelBtn = undefined;
            this._saveBtn = undefined;
            this._sshKexAlgorithmsEl = undefined;
            this._sshMacEl = undefined;
            this._sshV1Checkbox = undefined;
            this._sshCipherEl = undefined;
        });
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _showHideOptionsForm() {
        if (this._model._osmOptionsAvailable) {
            this._osmOptionFormContainer.classList.remove("hidden");
            this._noOptionsEl.classList.add("hidden");
            this._vendorOsDropDownsEl.classList.remove("hidden");
        } else {
            this._osmOptionFormContainer.classList.add("hidden");
            this._noOptionsEl.classList.remove("hidden");
            this._vendorOsDropDownsEl.classList.add("hidden");
        }
    }
    _renderPrefs() {
        const _contentSection = document.createElement("container-main");

        /* Root append: Start */
        _contentSection.appendChild(this._createFileServerSection());
        _contentSection.appendChild(this._createProtocolControlSection());
        _contentSection.appendChild(this._createSSHSettingsControlSection());
        _contentSection.appendChild(this._createCatalogSection());
        _contentSection.appendChild(this._createPlanExecSection());

        _contentSection.appendChild(this._createOsmOptionsSection());

        this._rootElem.appendChild(_contentSection);
        /* Root append: End */
        this._showControls();
        // this._osmOptionFormContainer.appendChild(this._formsComponent.renderer.rootElement);
        // this._formsComponent.setTypeInformation(this._getOptsJtype("*", "*"), true);
    }

    _getOptsJtype(vendor, os) {
        return this._model._osmOptions[vendor][os].jtype;
    }

    _initializeSlickGrid(imageServerData) {
        this._slickTable.init();
        this._slickTable.initialized = true;

        this._slickTableContainer.appendChild(this._defaultMessageContainer);
        this._setSlickData(imageServerData);
    }

    _createFileServerSection() {
        const _fileServerSection = document.createDocumentFragment();

        const _gridContainer = document.createDocumentFragment();
        _gridContainer.appendChild(this._slickTableContainer);
        _gridContainer.appendChild(this._downloadActivityLogsBtn.renderer.getRootElement());

        const enableFileServerElem = uiUtils.wrapElement(
            this._fileServersEnabledCheckBox.renderer.rootElement,
            "",
            "enable-file-server"
        );

        this._fileServersEnabledCheckBox.setVisible(
            this._model._getEnableDisableImageServerConfig()
        );

        _fileServerSection.appendChild(enableFileServerElem);

        _fileServerSection.appendChild(uiUtils.wrapElement(_gridContainer, "", "file-servers"));

        return this._createSection(_fileServerSection, {
            title: "File Servers",
            className: "file-server-section",
            bordered: true,
        });
    }

    _enableDisableProtocols(isDisabled) {
        this.adminPortInput.setDisabled(isDisabled);
        this.scpInput.setDisabled(isDisabled);
        this._ftpCheckbox.setDisabled(isDisabled);
        this.ftpInput.setDisabled(isDisabled || !this._ftpCheckbox.getValue());
        this._enableAnonymousFtpCheckbox.setDisabled(isDisabled || !this._ftpCheckbox.getValue());
        this._tftpCheckbox.setDisabled(isDisabled);
        this.tftpInput.setDisabled(isDisabled || !this._tftpCheckbox.getValue());
        this._sshKexAlgorithmsEl.setDisabled(isDisabled);
        this._sshCipherEl.setDisabled(isDisabled);
        this._sshMacEl.setDisabled(isDisabled);
        this._sshV1Checkbox.setDisabled(isDisabled);
        this._setResetSSHBtnState();
    }

    _createSSHSettingsControlSection() {
        const fragment = document.createDocumentFragment();
        const sskkexFrag = uiUtils.wrapElement(
            this._sshKexAlgorithmsEl.renderer.getRootElement(),
            "SSH Kex Algorithms",
            ""
        );
        fragment.appendChild(uiUtils.wrapElement(sskkexFrag, "", "kex-algorithms"));

        const sshCiphFrag = uiUtils.wrapElement(
            this._sshCipherEl.renderer.getRootElement(),
            "SSH Ciphers",
            ""
        );
        fragment.appendChild(uiUtils.wrapElement(sshCiphFrag, "", "ssh-ciphers"));

        const sshMacFrag = uiUtils.wrapElement(
            this._sshMacEl.renderer.getRootElement(),
            "MACs",
            ""
        );
        fragment.appendChild(uiUtils.wrapElement(sshMacFrag, "", "ssh-macs"));

        const sshV1frag = document.createDocumentFragment();
        sshV1frag.appendChild(this._sshV1Checkbox.renderer.getRootElement());
        fragment.appendChild(uiUtils.wrapElement(sshV1frag, "", "ssh-v1-checkbox"));
        fragment.appendChild(this._resetSShPrefsBtn.renderer.getRootElement());

        return this._createSection(fragment, {
            title: "SSH Protocols",
            className: "protocol-control-section ssh-settings-section",
            bordered: true,
        });
    }

    _createProtocolControlSection() {
        const isProtocolDisabled =
            this._model._osImageServerPrefs._tenantId !== this._model.activeOrgId;
        this._enableDisableProtocols(isProtocolDisabled);
        const _scpPort = uiUtils.wrapElement(this.scpInput.renderer.rootElement, "SCP Port");
        const _anonymousFtp = uiUtils.wrapElement(
            this._enableAnonymousFtpCheckbox.renderer.rootElement,
            ""
        );
        const _hostSshPort = uiUtils.wrapElement(
            this.adminPortInput.renderer.rootElement,
            "Administrative Port"
        );
        // const _enableAnonymousFtp = uiUtils.wrapElement(
        //     this.adminPortInput.renderer.rootElement,
        //     "Administrative Port"
        // );
        const fragment = document.createDocumentFragment();
        const tftpFragment = document.createDocumentFragment();
        const ftpFragment = document.createDocumentFragment();
        fragment.appendChild(uiUtils.wrapElement(_hostSshPort, "", "admin-port-control"));
        fragment.appendChild(uiUtils.wrapElement(_scpPort, "", "scp-port-control"));
        ftpFragment.appendChild(this._ftpCheckbox.renderer.getRootElement());
        ftpFragment.appendChild(this.ftpInput.renderer.getRootElement());
        fragment.appendChild(uiUtils.wrapElement(ftpFragment, "", "ftp-port-control"));
        const anonymousFtpElement = uiUtils.wrapElement(
            this._enableAnonymousFtpCheckbox.renderer.getRootElement()
        );
        anonymousFtpElement.className = "indent-checkbox";
        fragment.appendChild(anonymousFtpElement);
        tftpFragment.appendChild(this._tftpCheckbox.renderer.getRootElement());
        tftpFragment.appendChild(this.tftpInput.renderer.getRootElement());
        fragment.appendChild(uiUtils.wrapElement(tftpFragment, "", "tftp-port-control"));

        fragment.appendChild(uiUtils.wrapElement(_anonymousFtp, "", "anonymous-ftp-port-control"));

        return this._createSection(fragment, {
            title: "Port Settings",
            className: "protocol-control-section",
            bordered: true,
        });
    }

    _createCatalogSection() {
        const _catalogsSection = document.createDocumentFragment();
        _catalogsSection.appendChild(this._enableCatalogsCheckbox.renderer.getRootElement());
        _catalogsSection.appendChild(this._enableCiscoValidationCheckbox.renderer.getRootElement());

        return this._createSection(_catalogsSection, {
            className: "catalog-section",
            title: "Image Selection & Validation",
            bordered: true,
        });
    }

    _createOsmOptionsSection() {
        const _osmOptions = document.createDocumentFragment();
        _osmOptions.appendChild(this._noOptionsEl);
        this._vendorOsDropDownsEl.appendChild(
            uiUtils.wrapElement(this._vendorDropdown.renderer.getRootElement(), "Vendor")
        );
        this._vendorOsDropDownsEl.appendChild(
            uiUtils.wrapElement(this._osDropdown.renderer.getRootElement(), "OS")
        );
        _osmOptions.appendChild(this._vendorOsDropDownsEl);
        _osmOptions.appendChild(this._osmOptionFormContainer);

        return this._createSection(_osmOptions, {
            className: "osm-options-section",
            title: "Options",
            bordered: true,
        });
    }

    _createPlanExecSection() {
        const _planExecutionSection = document.createDocumentFragment();

        this._failureThresholdInput.renderer.rootElement.classList.add("abort-on-failure");
        this._abortOnFailureCheckbox.renderer.rootElement.classList.add(
            "abort-on-failure-checkbox"
        );
        this._abortOnFailureCheckbox.renderer.rootElement.title =
            "Halts plan execution when percentage of devices cannot be updated.";

        const _abortOnFailureWrapper = document.createElement("div");
        _abortOnFailureWrapper.className = "abort-on-failure-wrapper";
        _abortOnFailureWrapper.appendChild(this._abortOnFailureCheckbox.renderer.getRootElement());
        _abortOnFailureWrapper.appendChild(this._failureThresholdInput.renderer.getRootElement());

        this._linearJobExecutionCheckbox.renderer.rootElement.title =
            "Updates devices in the plan one after another.";

        _planExecutionSection.appendChild(
            this._linearJobExecutionCheckbox.renderer.getRootElement()
        );
        _planExecutionSection.appendChild(_abortOnFailureWrapper);
        const _reExecutionWrapper = document.createElement("div");
        _reExecutionWrapper.classList.add("re-execution-wrapper");
        _reExecutionWrapper.appendChild(this._reExecutionCheckbox.renderer.getRootElement());
        _planExecutionSection.appendChild(_reExecutionWrapper);

        const _skipValidationWrapper = document.createElement("div");
        _skipValidationWrapper.className = "skip-validation-wrapper";
        _skipValidationWrapper.appendChild(this._skipValidationCheckbox.renderer.getRootElement());
        _planExecutionSection.appendChild(_skipValidationWrapper);

        return this._createSection(_planExecutionSection, {
            title: "Plan Execution",
            className: "plan-exec-section",
            bordered: true,
        });
    }

    _createSection(content, options = {}) {
        const section = document.createElement("section");
        section.className = options.className || "";
        section.classList.toggle("bordered", !!options.bordered);

        if (options.title) {
            const title = document.createElement("h2");
            title.innerText = options.title;
            section.appendChild(title);
        }
        section.appendChild(content);
        return section;
    }

    _showControls() {
        this._actionsContainer.appendChild(this._cancelBtn.renderer.getRootElement());
        this._actionsContainer.appendChild(this._saveBtn.renderer.getRootElement());

        this._rootElem.appendChild(this._actionsContainer);
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/
    _clickHandler(e) {
        //if we click delete column
        const target = e.target;
        if (target.getAttribute("role") === "button" && !target.getAttribute("disabled")) {
            const row = target.getAttribute("row");
            const data = this._slickTable.getData()[row];
            switch (target.getAttribute("action")) {
                case "edit":
                    this._editFileServer(data);
                    break;
                case "delete":
                    this._deleteFileServer(data);
                    break;
                case "refresh":
                    this._refreshFileServer(data);
                    break;
                case "getKeys":
                    this._showKeys(data);
                    break;
                default:
                    break;
            }
        }
        e.stopPropagation();
    }

    _submitAction() {
        //User needs to be informed if ports are changed
        //submit will still happen
        const patches = this._model._osImageServerPrefs.pendingPatches;
        const watchedFields = [
            "/ftpPort",
            "/tftpPort",
            "/anonymousFtp",
            "/protocols/ftp",
            "/protocols/tftp",
            "/adminPort",
            "/scpPort",
        ];
        let portTouched = false;

        watchedFields.forEach((field) => {
            if (_.findIndex(patches, {path: field}) >= 0) {
                portTouched = true;
            }
        });
        if (portTouched) {
            this._model.userFeedback.displayNotification(
                "It takes up to five minutes for ports to be reconfigured. Please wait before using the new values.",
                {
                    title: "Warning",
                    notDismissable: true,
                    confirmDetails: {text: "OK"},
                }
            );
        }

        const enableCatalogPatch = this._model._osUpgradePlanPrefs.pendingPatches
            ? this._model._osUpgradePlanPrefs.pendingPatches.find(
                  (patch) => patch.path === "/enableCatalogs"
              )
            : undefined;

        this._model
            .saveUpgradePrefs()
            .then(() => {
                this._setPristineProperties();
                this._setResetSSHBtnState();
            })
            .then(() => {
                if (!_.isUndefined(enableCatalogPatch)) {
                    this._model._uiUtils
                        .showMessageDialog({
                            message:
                                "You have made changes to the Enable Catalog preference. The browser will be refreshed.",
                            confirmButtonName: "OK",
                        })
                        .then(() => {
                            this._model._router.reload();
                        });
                }
                return Promise.resolve();
            })
            .catch((error) => {
                if (!error) {
                    return;
                }
                if (error.errorCode === "NoPendingUpdates") {
                    this._toggleButtons(false);
                } else {
                    return this._model && this._model.userFeedback.displayError(error);
                }
            });
    }

    _cancelAction() {
        this.model.resetPrefs();
        this._setPristineProperties();
        this._setResetSSHBtnState();
    }

    _addNewFileServer() {
        this._openImageServerEditor(null);
    }

    _onResetSSHPrefs() {
        const msg =
            "Are you sure you want to reset SSH settings to Gluware's defaults? These are the recommended settings.";
        return this._model._modalUserFeedback
            .displayConfirmation(msg, {
                title: "Warning",
                confirmDetails: {
                    text: "Reset",
                    type: "destructive",
                },
                rejectDetails: {
                    text: "Cancel",
                    type: "text",
                },
            })
            .then(() => {
                this._sshKexAlgorithmsEl.setValue(SSH_KEX_DEF);
                this._sshMacEl.setValue(SSH_MAC_DEF);
                this._sshCipherEl.setValue(SSH_CIPHER_DEF);
                this._sshV1Checkbox.setValue(false);
                this._updatePrefs();
                return Promise.resolve();
            })
            .catch(() => {
                this._model._logger.warn("OS Management :: SSH resets :: User Rejected");
                return Promise.resolve();
            });
    }

    _downloadActivityLogs() {
        this._downloadActivityLogsBtn.setDisabled(true);
        this.model
            .downloadServersActivityLogs()
            .then(
                () => _.invoke(this._downloadActivityLogsBtn, "setDisabled", false),
                (error) => this._model && this._model.userFeedback.displayError(error)
            )
            .catch(() => _.invoke(this._downloadActivityLogsBtn, "setDisabled", false));
    }

    _editFileServer(data) {
        this._openImageServerEditor(data);
    }

    _deleteFileServer(imageServerData) {
        const {identifier} = imageServerData;
        const {
            id,
            isMain = identifier === "MAIN" ? true : false,
            name = "Unknown",
        } = imageServerData;
        const deleteMessage = isMain ? DELETE_MAIN_CONFIRMATION : DELETE_CONFIRMATION(name);

        return this._model.userFeedback
            .displayConfirmation(deleteMessage, {
                title: "Delete",
                confirmDetails: {
                    text: "Confirm",
                    type: "destructive",
                },
                rejectDetails: {
                    text: "Cancel",
                    type: "text",
                },
                notDismissable: true,
                dontHide: true,
            })
            .then(() => {
                const loadingVeil = () =>
                    this._model.userFeedback.displayLoading({
                        message: "Deleting the server... This could take a moment",
                    });
                return Promise.all([loadingVeil(), this._model.deleteImageServer(id)]);
            })
            .catch((error) => {
                if (error && !error.userRejected) {
                    console.log("Error in Delete: _deleteFileServer()", error);
                    return this._model && this._model.userFeedback.displayError(error);
                }
            })
            .then(() => this._model && this._model.userFeedback.hideDisplayed());
    }

    _refreshFileServer(imageServerData) {
        const servUpdatedOn = this._model._imageServersFeed.getInstanceById(
            imageServerData.id
        )._updatedOn;
        const timeNow = Date.now();
        const fiveMin = 1000 * 60 * 5; //5 minutes in milliseconds
        let timeoutErr = true;

        // check to see if server is offline and updated less than 5 min ago...
        // if so, we need to let the user know they need to wait
        if (
            servUpdatedOn &&
            imageServerData.status === "OFFLINE" &&
            timeNow - servUpdatedOn < fiveMin
        ) {
            return this._model.userFeedback.displayNotification(
                "Server not ready.  Please wait five minutes, and try again."
            );
        }

        const {identifier} = imageServerData;
        const {
            id,
            isMain = identifier === "MAIN" ? true : false,
            name = "Unknown",
        } = imageServerData;
        const refreshMessage = isMain ? REFRESH_MAIN_CONFIRMATION : REFRESH_CONFIRMATION(name);

        return this._model.userFeedback
            .displayConfirmation(refreshMessage, {
                title: "Refresh",
                confirmDetails: {
                    text: "Confirm",
                    type: "text",
                },
                rejectDetails: {
                    text: "Cancel",
                    type: "text",
                },
                notDismissable: true,
                dontHide: true,
            })
            .then(() => {
                const loadingVeil = () =>
                    this._model.userFeedback
                        .displayLoading({
                            message: "Refreshing the server... This could take a moment",
                        })
                        .then(
                            new Promise(() =>
                                _.delay(() => {
                                    //User should wait no more than 5 min
                                    if (timeoutErr) {
                                        this._model.userFeedback.displayError({
                                            title: "Looks like there was a problem",
                                            errorMsg: "The file server could not be contacted.",
                                        });
                                    }
                                }, fiveMin)
                            )
                        );
                return Promise.all([loadingVeil(), this._model.refreshFileServer(id)]);
            })
            .catch((error) => {
                if (error && !error.userRejected) {
                    return this._model && this._model.userFeedback.displayError(error);
                }
            })
            .then(() => {
                timeoutErr = false;
                return this._model && this._model.userFeedback.hideDisplayed();
            });
    }

    _showKeys(imageServerData) {
        const keysDataPromise = this._model.getKeys(imageServerData.id);
        const showKeysDialog = new ShowKeysDialog(keysDataPromise);
        this._model.userFeedback
            .displayCustom(showKeysDialog, {notDismissable: false})
            .catch((error) => {
                if (error && !error.userRejected) {
                    console.log("Error in ShowKeys: _showKeys()", error);
                }
            });
    }

    _onFeedUpdate(feed) {
        this._setSlickData(this._convertFeedToSlickData(feed));
        this._toggleDownloadActivityLogs(!_.isEmpty(feed.cache) && this.model.fileServersEnabled);
    }

    _onOrganizationChange(/*org*/) {
        this.model.userFeedback.hideDisplayed();
        this._fileServersEnabledCheckBox.setVisible(
            this._model._getEnableDisableImageServerConfig()
        );
    }

    _onVendorDropdownChange(val) {
        const osOptions = this._model.generateOsmOptionsOsDDOpts(val);
        this._osDropdown.setOptions(osOptions);
        this._osDropdown.setValue(osOptions[0].value);
        this._onOsDropdownChange(osOptions[0].value);
    }

    _onOsDropdownChange() {
        const jtype = this._getOptsJtype(
            this._vendorDropdown.getValue(),
            this._osDropdown.getValue()
        );
        const wrapJtype = {};

        const vendor = this._vendorDropdown
            .getOptions()
            .find((obj) => obj.value === this._vendorDropdown.getValue());
        const os = this._osDropdown
            .getOptions()
            .find((obj) => obj.value === this._osDropdown.getValue());
        let wrapJtypeLabel = "Options";

        if (vendor && vendor.value === "*" && os && os.value === "*") {
            wrapJtypeLabel = "Common Options";
        } else {
            const vendorLabel = vendor ? vendor.label : "";
            const osLabel = os ? os.label : "";
            wrapJtypeLabel = `${vendorLabel} ${osLabel} Options`.trim();
        }

        Object.assign(
            wrapJtype,
            {
                "@Display.layout": {},
            },
            {
                "@Display.styles": {
                    cssClasses: ["dark"],
                },
            }
        );

        wrapJtype[wrapJtypeLabel] = jtype;
        this._osmOptionJtypeKey = wrapJtypeLabel;
        this._formsComponent.setTypeInformation(wrapJtype, false);
        const formPristineVal = this._getFormPristineValue();
        if (formPristineVal) {
            setTimeout(() => this._formsComponent.setPristineValue(formPristineVal), 10);
        }
    }
    _onDirtyState(state) {
        this._toggleButtons(state);
    }

    _onDataUpdated(type) {
        this._setPristineProperties();
        this._enableDisableProtocols(
            this._model._osImageServerPrefs._tenantId !== this._model.activeOrgId
        );
        if (type === "OrgChange") {
            try {
                if (this._model._osmOptionsAvailable) {
                    this._vendorDropdown.setOptions(this._model.generateOsmOptionsVendorDDOpts());
                    this._osDropdown.setOptions(this._model.generateOsmOptionsOsDDOpts("*"));
                    this._vendorDropdown.setValue("*", true);
                    this._onVendorDropdownChange("*");
                    this._formsComponent.renderer.render();
                }
                this._showHideOptionsForm();
            } catch (err) {
                this._model._logger.warn("Options not ready" + err);
            }
        }
    }
    _onOrgDataChanged() {
        //this._fileServersEnabledCheckBox.setVisible(!this.model.activeOrgIsRoot);
    }

    _onFormsPendingPatchesChanged() {
        const formVals = _.get(this._formsComponent.getValue(), this._osmOptionJtypeKey, {});
        const vendor = this._vendorDropdown.getValue();
        const os = this._osDropdown.getValue();
        let osmOptions = _.get(this._model._osUpgradePlanPrefs, "osmOptions", {});
        if (vendor === "*" && os === "*") {
            osmOptions = _.set(osmOptions, `commonOptions`, formVals);
        } else {
            osmOptions = _.set(osmOptions, `vendorOsOptions.${vendor}.${os}.options`, formVals);
        }
        this._model._osUpgradePlanPrefs.setOsmOptions(osmOptions);
    }

    /* Control change events : Start */
    _fileServersEnabledChange(value) {
        this._changeFileServerEnabledState(value);
    }
    _catalogEnabledChange() {
        this._updatePrefs(true);
    }
    _ciscoValidationEnabledChange() {
        this._updatePrefs();
    }
    _anonymousFtpEnabledChange() {
        this._updatePrefs();
    }
    _ftpEnabledChange() {
        this._updatePrefs();
    }
    _tftpEnabledChange() {
        this._updatePrefs();
    }
    _tftpPortChange() {
        this._updatePrefs();
    }
    _ftpPortChange() {
        this._updatePrefs();
    }
    _scpPortChange() {
        this._updatePrefs();
    }
    _onSSHChange() {
        this._updatePrefs();
    }
    _hostPortChange() {
        this._updatePrefs();
    }
    _linearJobExecutionChange() {
        this._updatePrefs();
    }
    _reExecutionChange() {
        this._updatePrefs();
    }
    _skipValidationChange() {
        this._updatePrefs();
    }
    _abortOnFailureChange(value) {
        this._toggleAbortOnFailureThreshold(value);
        this._updatePrefs();
    }
    _failureThresholdInputChange() {
        this._updatePrefs();
    }
    /* Control change events : End */
    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/
    _convertFeedToSlickData(feed) {
        const feedCache = feed.getCacheAsArray();
        const slickFormattedData = feedCache.map((osImageServer) =>
            Object.assign({id: osImageServer.id}, osImageServer.getPristineProperties())
        );
        this._mainExists = slickFormattedData.some((item) => item.identifier === "MAIN");

        return slickFormattedData;
    }

    _setSlickData(newData) {
        return new Promise((resolve) => {
            this._slickTable.setData(newData);
            this._slickTable.invalidate();
            this._showHideDefaultMessage(newData);
            this._slickTable.resizeCanvas();
            return resolve();
        });
    }

    _setPristineProperties() {
        /* Hiding the enableCiscoValidationCheckbox here based on cisco api enabled value from vendor api */
        if (!this._model._isCiscoEnabled) {
            this._enableCiscoValidationCheckbox.hide();
        } else {
            this._enableCiscoValidationCheckbox.show();
        }
        const pristineProperties = this.model.osPrefsData;

        this._fileServersEnabledCheckBox.setValue(this.model.fileServersEnabled);

        /* File Server : Start */
        this._ftpCheckbox.setValue(_.get(pristineProperties, "protocols.ftp"));
        this._tftpCheckbox.setValue(_.get(pristineProperties, "protocols.tftp"));
        this.ftpInput.setValue(_.get(pristineProperties, "ftpPort"));
        this.scpInput.setValue(_.get(pristineProperties, "scpPort"));
        this.tftpInput.setValue(_.get(pristineProperties, "tftpPort"));
        this.adminPortInput.setValue(_.get(pristineProperties, "adminPort"));
        this._enableAnonymousFtpCheckbox.setValue(_.get(pristineProperties, "anonymousFtp"));
        this._sshKexAlgorithmsEl.setValue(
            _.get(pristineProperties, "sshKexAlgorithms", SSH_KEX_DEF)
        );
        this._sshCipherEl.setValue(_.get(pristineProperties, "sshCiphers", SSH_CIPHER_DEF));
        this._sshMacEl.setValue(_.get(pristineProperties, "sshMACs", SSH_MAC_DEF));
        this._sshV1Checkbox.setValue(_.get(pristineProperties, "sshProtocolOneEnabled"));
        /* File Server : End */

        /* Catalog Section : Start */
        this._enableCatalogsCheckbox.setValue(_.get(pristineProperties, "enableCatalogs"));
        this._enableCiscoValidationCheckbox.setValue(
            !_.get(pristineProperties, "skipCiscoApiImageChecks", false)
        );
        /* Catalog Section : End */

        this._linearJobExecutionCheckbox.setValue(_.get(pristineProperties, "linear"));
        this._reExecutionCheckbox.setValue(_.get(pristineProperties, "allowMultipleExecutions"));
        this._skipValidationCheckbox.setValue(_.get(pristineProperties, "skipValidation"));
        this._abortOnFailureCheckbox.setValue(_.get(pristineProperties, "abortOnFailure"));
        this._failureThresholdInput.setValue(_.get(pristineProperties, "failureThreshold"));

        const formPristineVal = this._getFormPristineValue();
        if (formPristineVal) {
            this._formsComponent.setPristineValue(formPristineVal);
        }
        this._toggleServerControls(this.model.fileServersEnabled);
        this._toggleAbortOnFailureThreshold(this._abortOnFailureCheckbox.value);
    }

    _getFormPristineValue() {
        const vendor = this._vendorDropdown.getValue();
        const os = this._osDropdown.getValue();
        let formPristineVal;
        if (vendor === "*" && os === "*") {
            formPristineVal = _.get(
                this._model._osUpgradePlanPrefs,
                `_pristineProperties.osmOptions.commonOptions`,
                undefined
            );
        } else {
            formPristineVal = _.get(
                this._model._osUpgradePlanPrefs,
                `_pristineProperties.osmOptions.vendorOsOptions.${vendor}.${os}.options`,
                undefined
            );
        }

        let formVal = {};
        formVal[this._osmOptionJtypeKey] = formPristineVal;
        if (formPristineVal) {
            return formVal;
        }
        return null;
    }

    _updatePrefs() {
        const sshkex = this._sshKexAlgorithmsEl.getValue();
        const sshCiph = this._sshCipherEl.getValue();
        const sshMac = this._sshMacEl.getValue();
        const formData = Object.assign(
            {},
            {
                enableCatalogs: this._enableCatalogsCheckbox.value,
                skipCiscoApiImageChecks: !this._enableCiscoValidationCheckbox.value,
                protocols: {
                    ftp: this._ftpCheckbox.value,
                    tftp: this._tftpCheckbox.value,
                },
                scpPort: this.scpInput.value,
                adminPort: this.adminPortInput.value,
                anonymousFtp: this._enableAnonymousFtpCheckbox.value,
                sshKexAlgorithms: Array.isArray(sshkex) && sshkex.length > 0 ? sshkex : undefined,
                sshCiphers: Array.isArray(sshCiph) && sshCiph.length > 0 ? sshCiph : undefined,
                sshMACs: Array.isArray(sshMac) && sshMac.length > 0 ? sshMac : undefined,
                sshProtocolOneEnabled: this._sshV1Checkbox.value,
            },
            !this.ftpInput.disabled && {ftpPort: this.ftpInput.value},
            !this.tftpInput.disabled && {tftpPort: this.tftpInput.value},
            !this._linearJobExecutionCheckbox.disabled && {
                linear: this._linearJobExecutionCheckbox.value,
            },
            !this._reExecutionCheckbox.disabled && {
                allowMultipleExecutions: this._reExecutionCheckbox.value,
            },
            !this._skipValidationCheckbox.disabled && {
                skipValidation: this._skipValidationCheckbox.value,
            },
            !this._abortOnFailureCheckbox.disabled && {
                abortOnFailure: this._abortOnFailureCheckbox.value,
            },
            !this._failureThresholdInput.disabled && {
                failureThreshold: this._failureThresholdInput.value,
            }
        );

        if (!formData.sshKexAlgorithms) {
            this._sshKexAlgorithmsEl.setValue(SSH_KEX_DEF);
            formData.sshKexAlgorithms = SSH_KEX_DEF;
        }

        if (!formData.sshMACs) {
            this._sshMacEl.setValue(SSH_MAC_DEF);
            formData.sshMACs = SSH_MAC_DEF;
        }

        if (!formData.sshCiphers) {
            this._sshCipherEl.setValue(SSH_CIPHER_DEF);
            formData.sshCiphers = SSH_CIPHER_DEF;
        }

        this.model.updatePrefs(formData);
        this._setResetSSHBtnState();
    }

    _setResetSSHBtnState() {
        if (this._isSSHDefault() || !this.model.fileServersEnabled) {
            this._resetSShPrefsBtn.disabled = true;
        } else {
            this._resetSShPrefsBtn.disabled = false;
        }
    }

    _fitTableSize() {
        if (this._slickTable) {
            this._slickTable.resizeCanvas();
        }
    }

    _toggleButtons(state) {
        this._cancelBtn.setDisabled(!state);
        this._saveBtn.setDisabled(!state);
    }

    _toggleServerControls(state) {
        this._toggleDownloadActivityLogs(!_.isEmpty(this.model.imageServersFeed.cache) && state);
        this._slickTable.setColumns(this._columns.filter((col) => state || col.id !== "actions"));
        this._slickTable.resizeCanvas();
    }

    _toggleAbortOnFailureThreshold(state) {
        this._failureThresholdInput.setDisabled(!state);
    }

    _toggleDownloadActivityLogs(state) {
        this._downloadActivityLogsBtn.setVisible(state);
    }

    _showHideDefaultMessage(gridData) {
        this._defaultMessageContainer.classList.toggle("hide", !_.isEmpty(gridData));
    }

    _isSSHDefault() {
        let isDefault = true;
        const sshProps = {
            sshKexAlgorithms: this._sshKexAlgorithmsEl.getValue() || SSH_KEX_DEF,
            sshCiphers: this._sshCipherEl.getValue() || SSH_CIPHER_DEF,
            sshMACs: this._sshMacEl.getValue() || SSH_MAC_DEF,
            sshProtocolOneEnabled: this._sshV1Checkbox.value,
        };
        const def = {
            sshKexAlgorithms: SSH_KEX_DEF,
            sshCiphers: SSH_CIPHER_DEF,
            sshMACs: SSH_MAC_DEF,
            sshProtocolOneEnabled: false,
        };
        Object.keys(sshProps).forEach((key) => {
            if (key !== "sshProtocolOneEnabled") {
                const newval = [...sshProps[key]];
                const oldval = [...def[key]];
                if (!_.isEqual(newval.sort(), oldval.sort())) {
                    isDefault = false;
                }
            } else {
                if (sshProps.sshProtocolOneEnabled !== def.sshProtocolOneEnabled) {
                    isDefault = false;
                }
            }
        });
        return isDefault;
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/

    _openImageServerEditor(imageServerData) {
        const component = new editDialog({
            entityDef: this._model.OsImageServersEntity,
            imageServer: imageServerData,
            imageServerPrefs: this._model.osPrefsData,
            activeOrgId: this._model.activeOrgId,
            isMain: !this._mainExists || _.get(imageServerData, "isMain", false),
            fileServerCredentialName: this._model._credentialName,
        });
        this._model.userFeedback
            .displayCustom(component, {notDismissable: false})
            .catch((error) => {
                if (error && !error.userRejected) {
                    console.error("Error in ImageServerEditor", imageServerData, error);
                }
            });
    }

    _changeFileServerEnabledState(value) {
        const enableWithNoUpstream = value === true && this.model.hasNoUpstreamFileServers;
        const enableWithUpstream = value === true && !this.model.hasNoUpstreamFileServers;
        const disable = value === false;

        const message =
            (enableWithNoUpstream && ENABLE_FILE_SERVERS_WITH_NO_UPSTREAM_WARNING) ||
            (enableWithUpstream && ENABLE_FILE_SERVERS_WITH_UPSTREAM_WARNING) ||
            (disable && DISABLE_FILE_SERVERS_WARNING_MESSAGE);

        const title = value ? ENABLE_FILE_SERVERS_TITLE : DISABLE_FILE_SERVERS_TITLE;

        const confirmation = () =>
            this.model.userFeedback.displayConfirmation(message, {
                title,
                enableHtml: true,
                confirmDetails: {
                    text: "Confirm",
                    type: enableWithNoUpstream ? "additive" : "destructive",
                },
                rejectDetails: {
                    text: "Cancel",
                    type: "text",
                },
                notDismissable: true,
                dontHide: true,
            });

        return confirmation()
            .then(() => {
                return this.model.enableFileServer(!!value);
            })
            .then(() => {
                return this.model._establishImageServersFeed(this.model._orgId).whenInitialized();
            })
            .then(() => {
                this._setSlickData(this._convertFeedToSlickData(this.model.imageServersFeed));
                this._toggleDownloadActivityLogs(
                    !_.isEmpty(this.model.imageServersFeed.cache) && this.model.fileServersEnabled
                );
                // return this._fileServersEnabledCheckBox.setValue(!value);
            })
            .catch((error) => {
                this._fileServersEnabledCheckBox.setValue(!value);
                if (error && !error.userRejected) {
                    console.error(
                        {error: error},
                        "OSManagementRenderer._changeFileServerEnabledState() Error"
                    );
                    return this.model.userFeedback.displayError(error);
                }
            })
            .finally(() => {
                // Only toggle if _fileServersEnabledCheckBox has been updated
                if (this._fileServersEnabledCheckBox.getValue() === value) {
                    Promise.all([
                        this._model.getOsUpgradePlanPrefs(),
                        this._model.getOsImageServerPrefs(),
                    ]).then(() => {
                        this.model.resetPrefs();
                        this._setPristineProperties();
                        this._enableDisableProtocols(!value);
                        this._toggleServerControls(value);
                        this.model.userFeedback.hideDisplayed();
                    });
                } else {
                    this.model.userFeedback.hideDisplayed();
                }
            });
    }
}

module.exports = OSManagementRenderer;
